import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  Invoice,
  InvoiceStatus,
  InvoiceStats,
  BatchGenerationResult,
  BatchOperationResult,
  InvoiceListQuery,
  InvoiceListResponse,
  CreateCustomInvoiceRequest,
  MarkAsPaidRequest,
} from 'shared';

export const invoiceApi = {
  async generateDraftBatch(year: number): Promise<BatchGenerationResult> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/generate-draft`,
      { year }
    );
    return response.data.data;
  },

  async generateDraftSingle(memberId: string, year: number): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/generate-draft/${memberId}`,
      { year }
    );
    return response.data.data;
  },

  async createCustom(data: CreateCustomInvoiceRequest): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/custom`,
      data
    );
    return response.data.data;
  },

  async list(query?: InvoiceListQuery): Promise<InvoiceListResponse> {
    const params = new URLSearchParams();

    if (query?.year) params.append('year', query.year.toString());
    if (query?.status) params.append('status', query.status);
    if (query?.memberId) params.append('memberId', query.memberId);
    if (query?.search) params.append('search', query.search);
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

    const response = await axios.get(
      `${API_BASE_URL}/api/invoices?${params.toString()}`
    );
    return response.data.data;
  },

  async getById(id: string): Promise<Invoice> {
    const response = await axios.get(
      `${API_BASE_URL}/api/invoices/${id}`
    );
    return response.data.data;
  },

  async update(id: string, data: { notes?: string }): Promise<Invoice> {
    const response = await axios.put(
      `${API_BASE_URL}/api/invoices/${id}`,
      data
    );
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await axios.delete(
      `${API_BASE_URL}/api/invoices/${id}`
    );
  },

  async approve(id: string): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/${id}/approve`,
      {}
    );
    return response.data.data;
  },

  async batchApprove(invoiceIds: string[]): Promise<BatchOperationResult> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/batch-approve`,
      { invoiceIds }
    );
    return response.data.data;
  },

  async send(id: string): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/${id}/send`,
      {}
    );
    return response.data.data;
  },

  async batchSend(invoiceIds: string[]): Promise<BatchOperationResult> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/batch-send`,
      { invoiceIds }
    );
    return response.data.data;
  },

  async resend(id: string): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/${id}/resend`,
      {}
    );
    return response.data.data;
  },

  async cancel(id: string): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/${id}/cancel`,
      {}
    );
    return response.data.data;
  },

  async markAsPaid(id: string, data: MarkAsPaidRequest): Promise<Invoice> {
    const response = await axios.post(
      `${API_BASE_URL}/api/invoices/${id}/mark-paid`,
      data
    );
    return response.data.data;
  },

  async downloadPDF(id: string, invoiceNumber: string): Promise<void> {
    const response = await axios.get(
      `${API_BASE_URL}/api/invoices/${id}/pdf`,
      {
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${invoiceNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async getPreviewBlob(id: string): Promise<string> {
    const response = await axios.get(
      `${API_BASE_URL}/api/invoices/${id}/preview`,
      {
        responseType: 'blob',
      }
    );

    // Create a blob URL that can be displayed in an iframe
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return window.URL.createObjectURL(blob);
  },

  async getStats(year: number): Promise<InvoiceStats> {
    const response = await axios.get(
      `${API_BASE_URL}/api/invoices/stats/${year}`
    );
    return response.data.data;
  },
};
